import React, { useState, useEffect, useRef } from "react";
import { TopNavigation as CloudscapeTopNavigation } from "@cloudscape-design/components";
import { ThemeSelector } from "../../components/ThemeSelector/ThemeSelector";
import { getCurrentTheme, Theme } from "../../themes/themeConfig";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/reducers/rootReducer";
import { redirectToLoginPage } from "../../utils/auth-provider";
import { clearUserInfo } from "../../redux/actions/userActions";
import { buildPortalUrl, resolveDomain } from "../../config/domain";
import { SYSTEMS, PORTAL_NAVIGATION_TITLE, type SystemMeta } from "../../config/systems";
import "./TopNavigation.css";

function getNavigationTitle(): string {
  const { system, isPortal } = resolveDomain();
  if (isPortal) return PORTAL_NAVIGATION_TITLE;
  const match = (Object.values(SYSTEMS) as SystemMeta[]).find((s) => s.key === system);
  return match?.navigationTitle ?? match?.label ?? "Manufacturing Smart Assistant";
}

const TopNavigation: React.FC = () => {
  const dispatch = useDispatch();
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<Theme>(getCurrentTheme());
  const supportRef = useRef<HTMLDivElement>(null);
  
  // Get user data from Redux store
  const { familyName, givenName, email, userName, token } = useSelector((state: RootState) => state.user);
  // Signed in once we have a username/token in the store. On the portal an
  // unauthenticated user has neither, so we show a "Log in" action instead of
  // the user menu.
  const isSignedIn = Boolean(userName || token);

  useEffect(() => {
    if (!showSupport) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (supportRef.current && !supportRef.current.contains(e.target as Node)) {
        setShowSupport(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSupport]);

  useEffect(() => {
    // Listen for theme changes
    const updateTheme = () => {
      setCurrentTheme(getCurrentTheme());
      // Force re-apply navigation styles
      applyNavigationStyles();
    };

    // Apply navigation styles on mount
    applyNavigationStyles();

    // Listen for custom theme change events
    window.addEventListener("themeChanged", updateTheme);

    return () => {
      window.removeEventListener("themeChanged", updateTheme);
    };
  }, []);

  const applyNavigationStyles = () => {
    const theme = getCurrentTheme();

    // Don't apply custom navigation styling for Black/Default theme
    if (theme.name === "default") {
      // Remove any custom styling to use CloudScape defaults
      setTimeout(() => {
        const selectors = [
          ".awsui-top-navigation",
          '[data-testid="top-navigation"]',
          ".awsui-app-layout-navigation",
          "header",
        ];

        selectors.forEach((selector) => {
          const elements = document.querySelectorAll(selector);
          elements.forEach((element: any) => {
            if (element.style) {
              element.style.removeProperty("background-color");
              element.style.removeProperty("border-bottom-color");
            }
          });
        });

        const textSelectors = [
          ".awsui-top-navigation .awsui-top-navigation-title",
          ".awsui-top-navigation .awsui-button",
          ".awsui-top-navigation button",
          ".awsui-top-navigation span",
        ];

        textSelectors.forEach((selector) => {
          const elements = document.querySelectorAll(selector);
          elements.forEach((element: any) => {
            if (element.style) {
              element.style.removeProperty("color");
            }
          });
        });
      }, 100);
      return;
    }

    // Apply styles with a slight delay to ensure DOM is ready
    setTimeout(() => {
      // Target all possible top navigation selectors
      const selectors = [
        ".awsui-top-navigation",
        '[data-testid="top-navigation"]',
        ".awsui-app-layout-navigation",
        "header",
      ];

      selectors.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element: any) => {
          if (element.style) {
            element.style.setProperty(
              "background-color",
              theme.colors.navigationBackground,
              "important"
            );
            element.style.setProperty(
              "border-bottom-color",
              theme.colors.navigationBorder,
              "important"
            );
          }
        });
      });

      // Target navigation text elements
      const textSelectors = [
        ".awsui-top-navigation .awsui-top-navigation-title",
        ".awsui-top-navigation .awsui-button",
        ".awsui-top-navigation button",
        ".awsui-top-navigation span",
      ];

      textSelectors.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element: any) => {
          if (element.style) {
            element.style.setProperty(
              "color",
              theme.colors.navigationText,
              "important"
            );
          }
        });
      });
    }, 100);
  };

  const handleThemeChange = () => {
    setShowThemeSelector(false);
    // Trigger theme update
    setTimeout(() => {
      setCurrentTheme(getCurrentTheme());
      applyNavigationStyles();
      // Dispatch custom event
      window.dispatchEvent(new CustomEvent("themeChanged"));
    }, 50);
  };

  return (
    <div
      style={{
        position: "relative",
        ...(currentTheme.name !== "default" && {
          backgroundColor: currentTheme.colors.navigationBackground,
          borderBottom: `1px solid ${currentTheme.colors.navigationBorder}`,
        }),
      }}
    >
      <div
        style={{
          ...(currentTheme.name !== "default" && {
            backgroundColor: currentTheme.colors.navigationBackground,
            color: currentTheme.colors.navigationText,
          }),
        }}
      >
        <CloudscapeTopNavigation
          identity={{
            href: "#",
            title: getNavigationTitle(),
            logo: {
              src: require('../../assets/images/project-logo.png'),
              alt: "MSA Logo",
            },
          }}
          utilities={[
            {
              type: "button",
              iconName: "settings",
              ariaLabel: "Theme Settings",
              onClick: () => setShowThemeSelector(!showThemeSelector),
            },
            {
              type: "button",
              text: "User Guide",
              href: "https://msa-user-guide.beta.harmony.a2z.com/",
              external: true,
              externalIconAriaLabel: "(opens in new tab)",
            },
            {
              type: "button",
              text: "Support",
              onClick: () => setShowSupport(!showSupport),
            },
            isSignedIn
              ? {
                  type: "menu-dropdown",
                  text: `${familyName}, ${givenName}`,
                  description: email || "No email available",
                  iconName: "user-profile",
                  onItemClick: ({ detail }) => {
                    if (detail.id === "logout") {
                      dispatch(clearUserInfo());
                      Object.keys(localStorage).forEach((key) => {
                        if (key.startsWith('CognitoIdentityServiceProvider')) {
                          localStorage.removeItem(key);
                        }
                      });
                      window.location.href = buildPortalUrl();
                    }
                  },
                  items: [
                    // { id: "profile", text: "Profile" },
                    // { id: "preferences", text: "Preferences" },
                    // { id: "notifications", text: "Notifications" },
                    { id: "logout", text: "Sign out" },
                  ],
                }
              : {
                  type: "button",
                  text: "Log in",
                  iconName: "user-profile",
                  ariaLabel: "Log in",
                  // Redirect to the identity provider; RedirectUtils saves the
                  // current URL so the user returns to this page after login.
                  onClick: () => {
                    redirectToLoginPage();
                  },
                },
          ]}
          visualContext="none"
        />
      </div>
      {showSupport && (
        <div ref={supportRef} className="support-popover">
          <h4 className="support-popover__title">Need help?</h4>
          <p className="support-popover__description">
            If you have any questions or need assistance, please contact us:
          </p>
          <p className="support-popover__contacts">
            <a href="mailto:sujiache@amazon.com" className="support-popover__link">
              sujiache@amazon.com
            </a>
            <br/>
            <a href="mailto:mkzhao@amazon.com" className="support-popover__link">
              mkzhao@amazon.com
            </a>
          </p>
        </div>
      )}
      {showThemeSelector && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: "240px",
            zIndex: 1000,
            marginTop: "8px",
          }}
        >
          <ThemeSelector onThemeChange={handleThemeChange} />
        </div>
      )}
    </div>
  );
};

export default TopNavigation;
